/**
 * Unified Communications Page — OpenPhone-style
 * Combines SMS, Calls, and Internal Chat into a single inbox
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RequireStaff } from '@/components/RequireStaff';
import { useAuth } from '@/contexts/AuthContext';
import { db, functions as firebaseFunctions } from '@/lib/firebase';
import {
  collection,
  query as fsQuery,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTwilioDevice } from '@/hooks/useTwilioDevice';
import { useAgentPresence } from '@/hooks/useAgentPresence';
import { SoftphoneBar } from '@/components/communications/SoftphoneBar';
import { SMSSnippetsPanel } from '@/components/communications/SMSSnippetsPanel';
import { MarketingContactForm } from '@/components/communications/MarketingContactForm';
import { MarketingDashboard } from '@/components/communications/MarketingDashboard';
import { AgentsOnlinePanel } from '@/components/communications/AgentsOnlinePanel';
import { ContactsDirectory } from '@/components/communications/ContactsDirectory';
import { MissedCallsDashboard } from '@/components/communications/MissedCallsDashboard';
import { PendingBookingsPanel } from '@/components/staff/PendingBookingsPanel';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext';
import { COMM_HUB_T } from '@/constants/communication.constants';

import {
  Search,
  Phone,
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Grid3X3,
  Delete,
  MessageSquare,
  Send,
  Plus,
  Loader2,
  User,
  Users,
  Settings,
  Activity,
  Contact,
  BarChart3,
  Inbox,
  MessageCircle,
  Voicemail,
  Filter,
  ChevronRight,
  X,
  Mic,
  Play,
  Pause,
  MoreHorizontal,
  CheckCheck,
  Circle,
  AlertCircle,
  Bot,
  Hash,
  Zap,
  UserPlus,
  Clock,
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

type InboxTab = 'chats' | 'calls' | 'voicemails';
type InboxFilter = 'all' | 'unread' | 'assigned';
type ConversationType = 'sms' | 'call' | 'chat';
type SidebarView = 'inbox' | 'contacts' | 'activity' | 'settings' | 'marketing' | 'dialpad' | 'missed' | 'agents' | 'bookings';

interface UnifiedThread {
  id: string;
  type: ConversationType;
  contact: string;
  contactId?: string;
  avatar?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  direction?: 'inbound' | 'outbound';
  callStatus?: string;
  callDuration?: number;
  isVoicemail?: boolean;
  voicemailUrl?: string;
  transcriptionText?: string;
  participants?: string[];
}

interface SMSMessage {
  id: string;
  message_sid: string;
  from_number: string;
  to_number: string;
  body: string;
  direction: string;
  status: string;
  is_read: boolean;
  created_at: string;
}

interface CallRecord {
  id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  status: string;
  duration_seconds: number | null;
  caller_name: string | null;
  created_at: string;
  recording_url: string | null;
  voicemail_url: string | null;
  transcription_text: string | null;
  assigned_agent_id: string | null;
  metadata: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

import AdminInbox from '@/components/shared/AdminInbox';

export default function StaffCommunications() {
  const [mainTab, setMainTab] = React.useState<'comms' | 'inbox'>('comms');

  return (
    <RequireStaff>
      <div>
        <div className="flex items-center bg-muted/50 rounded-lg p-1 gap-1 mb-4 w-fit">
          {([
            { id: 'comms', label: 'Communications', icon: Phone },
            { id: 'inbox', label: 'Inbox Plateforme', icon: Inbox },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setMainTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                mainTab === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
        {mainTab === 'comms' && <CommunicationsApp />}
        {mainTab === 'inbox' && <AdminInbox />}
      </div>
    </RequireStaff>
  );
}

export function CommunicationsApp() {
  const { language } = useLanguage();
  const t = COMM_HUB_T[language];
  const dateLocale = language === 'fr' ? fr : enUS;
  const { user } = useAuth();
  const {
    deviceStatus,
    activeCall,
    incomingCall,
    formattedDuration,
    makeCall,
    hangUp,
    toggleMute,
    acceptCall,
    rejectCall,
    initializeDevice,
  } = useTwilioDevice();

  // Bloc D — publish presence + read online agents (Firestore)
  const { availableAgents, busyAgents, awayAgents, setPresence } = useAgentPresence();
  useEffect(() => {
    if (activeCall || incomingCall) setPresence('busy');
    else setPresence('available');
  }, [activeCall, incomingCall, setPresence]);

  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Meeting context from Calendar redirect ────────────────────────
  const meetingContext = useMemo(() => {
    const meetingId = searchParams.get('meetingId');
    if (!meetingId) return null;

    const participantsRaw = searchParams.get('participants');
    let participants: { name: string; role: string; phone?: string; email?: string }[] = [];

    try {
      if (participantsRaw) {
        const parsed = JSON.parse(participantsRaw);
        if (Array.isArray(parsed)) {
          participants = parsed.map((p: any) => ({
            name: p?.name || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || 'Participant',
            role: p?.role || 'participant',
            phone: p?.phone || p?.phoneNumber || p?.phone_number || p?.mobile || undefined,
            email: p?.email || undefined,
          }));
        }
      }
    } catch {
      participants = [];
    }

    return {
      meetingId,
      meetingTitle: searchParams.get('meetingTitle') || '',
      name: searchParams.get('name') || '',
      phone: searchParams.get('phone') || '',
      email: searchParams.get('email') || '',
      meetingUrl: searchParams.get('meetingUrl') || '',
      format: searchParams.get('format') || 'video',
      participants,
    };
  }, [searchParams]);

  // State
  const [sidebarView, setSidebarView] = useState<SidebarView>('inbox');
  const [inboxTab, setInboxTab] = useState<InboxTab>('chats');
  const [inboxFilter, setInboxFilter] = useState<InboxFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [threads, setThreads] = useState<UnifiedThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<UnifiedThread | null>(null);
  const [smsMessages, setSmsMessages] = useState<SMSMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showContactPanel, setShowContactPanel] = useState(true);
  const [contactInfo, setContactInfo] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showSnippets, setShowSnippets] = useState(false);
  const [showMarketingForm, setShowMarketingForm] = useState(false);
  const [dialpadSavePhone, setDialpadSavePhone] = useState<string | undefined>(undefined);

  // ─── Load SMS threads (Firestore) ──────────────────────────────────
  const loadSMSThreads = useCallback(async (): Promise<UnifiedThread[]> => {
    try {
      const snap = await getDocs(
        fsQuery(collection(db, 'sms_messages'), orderBy('created_at', 'desc'))
      );
      const threadMap = new Map<string, UnifiedThread>();
      snap.forEach((d) => {
        const msg = { id: d.id, ...(d.data() as any) } as SMSMessage;
        const phone = msg.direction === 'inbound' ? msg.from_number : msg.to_number;
        if (!phone) return;
        const existing = threadMap.get(phone);
        if (!existing) {
          threadMap.set(phone, {
            id: `sms-${phone}`,
            type: 'sms',
            contact: phone,
            lastMessage: msg.body,
            lastMessageAt: msg.created_at,
            unreadCount: msg.direction === 'inbound' && !msg.is_read ? 1 : 0,
            direction: msg.direction as 'inbound' | 'outbound',
          });
        } else if (msg.direction === 'inbound' && !msg.is_read) {
          existing.unreadCount++;
        }
      });
      return Array.from(threadMap.values());
    } catch (err) {
      console.error('SMS load error:', err);
      return [];
    }
  }, []);

  // ─── Load call threads (Firestore `incoming_calls`) ────────────────
  const loadCallThreads = useCallback(async (): Promise<UnifiedThread[]> => {
    try {
      const snap = await getDocs(
        fsQuery(collection(db, 'incoming_calls'), orderBy('created_at', 'desc'))
      );
      const threadMap = new Map<string, UnifiedThread>();
      snap.forEach((d) => {
        const c = { id: d.id, ...(d.data() as any) } as any;
        const phone = c.from_number || c.to_number;
        if (!phone) return;
        const createdAt =
          typeof c.created_at === 'string'
            ? c.created_at
            : c.created_at?.toDate?.()?.toISOString?.() || new Date().toISOString();
        const isVoicemail = Boolean(c.voicemail_url);
        const isMissed = ['no-answer', 'busy', 'failed', 'missed'].includes(c.status);
        const existing = threadMap.get(phone);
        if (!existing) {
          threadMap.set(phone, {
            id: `call-${phone}`,
            type: 'call',
            contact: c.caller_name || phone,
            contactId: phone,
            lastMessage:
              c.transcription_text ||
              (isVoicemail ? (language === 'fr' ? 'Message vocal' : 'Voicemail') : c.status || 'call'),
            lastMessageAt: createdAt,
            unreadCount: isMissed || (isVoicemail && !c.is_read) ? 1 : 0,
            direction: 'inbound',
            callStatus: c.status,
            callDuration: c.duration_seconds || 0,
            isVoicemail,
            voicemailUrl: c.voicemail_url || undefined,
            transcriptionText: c.transcription_text || undefined,
          });
        }
      });
      return Array.from(threadMap.values());
    } catch (err) {
      console.error('Calls load error:', err);
      return [];
    }
  }, [language]);

  // ─── Load all threads ──────────────────────────────────────────────
  const loadAllThreads = useCallback(async () => {
    setLoading(true);
    try {
      const [sms, calls] = await Promise.all([loadSMSThreads(), loadCallThreads()]);

      const all = [...sms, ...calls].sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );
      setThreads(all);
    } catch (err) {
      console.error('Error loading threads:', err);
    } finally {
      setLoading(false);
    }
  }, [loadSMSThreads, loadCallThreads]);

  useEffect(() => { loadAllThreads(); }, [loadAllThreads]);

  // ─── Realtime SMS subscription (Firestore onSnapshot) ──────────────
  useEffect(() => {
    const unsub = onSnapshot(
      fsQuery(collection(db, 'sms_messages'), orderBy('created_at', 'desc')),
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const msg = change.doc.data() as any;
            if (msg.direction === 'inbound') {
              toast.info(`📩 ${t.newSmsFrom} ${formatPhone(msg.from_number)}`, {
                description: msg.body?.slice(0, 80) || t.messageReceived,
                duration: 6000,
              });
            }
          }
        });
        loadAllThreads();
      },
      (err) => console.error('SMS realtime error:', err)
    );
    // Bloc E — Marketing contacts realtime listener lives in MarketingDashboard.
    return () => { unsub(); };
  }, [loadAllThreads, t]);

  // ─── Load messages for selected SMS thread (Firestore) ─────────────
  const loadSMSMessages = useCallback(async (phone: string) => {
    try {
      const snap = await getDocs(
        fsQuery(collection(db, 'sms_messages'), orderBy('created_at', 'asc'))
      );
      const all: SMSMessage[] = [];
      snap.forEach((d) => {
        const m = { id: d.id, ...(d.data() as any) } as SMSMessage;
        if (m.from_number === phone || m.to_number === phone) all.push(m);
      });
      setSmsMessages(all);

      // Mark inbound as read
      const unread = all.filter(
        (m) => m.direction === 'inbound' && !m.is_read && m.from_number === phone
      );
      await Promise.all(
        unread.map((m) =>
          updateDoc(doc(db, 'sms_messages', m.id), { is_read: true }).catch(() => {})
        )
      );

      setThreads((prev) =>
        prev.map((th) => (th.contact === phone ? { ...th, unreadCount: 0 } : th))
      );
    } catch (err) {
      console.error('Error loading SMS messages:', err);
    }
  }, []);

  // ─── Select thread ────────────────────────────────────────────────
  const selectThread = useCallback((thread: UnifiedThread) => {
    setSelectedThread(thread);
    if (thread.type === 'sms') {
      loadSMSMessages(thread.contact);
    }
    loadContactInfo(thread.contactId || thread.contact);
  }, [loadSMSMessages]);

  // ─── Realtime calls subscription (Firestore onSnapshot) ────────────
  useEffect(() => {
    const unsub = onSnapshot(
      fsQuery(collection(db, 'incoming_calls'), orderBy('created_at', 'desc')),
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const c = change.doc.data() as any;
            if (['no-answer', 'busy', 'failed', 'missed'].includes(c.status)) {
              toast.info(
                `📞 ${language === 'fr' ? 'Appel manqué de' : 'Missed call from'} ${formatPhone(c.from_number)}`,
                { duration: 6000 }
              );
            } else if (c.voicemail_url) {
              toast.info(
                `🎙️ ${language === 'fr' ? 'Nouveau message vocal' : 'New voicemail'} — ${formatPhone(c.from_number)}`,
                { duration: 6000 }
              );
            }
          }
        });
        loadAllThreads();
      },
      (err) => console.error('Calls realtime error:', err)
    );
    return () => { unsub(); };
  }, [loadAllThreads, language]);

  // ─── Load contact info (Firestore `userProfiles` by phone/email) ───
  const loadContactInfo = async (identifier: string) => {
    try {
      const isPhone = identifier.startsWith('+');
      const field = isPhone ? 'phone' : 'email';
      const snap = await getDocs(
        fsQuery(collection(db, 'userProfiles'), where(field, '==', identifier))
      );
      if (!snap.empty) {
        const d = snap.docs[0].data() as any;
        setContactInfo({
          name:
            d.displayName ||
            [d.firstName, d.lastName].filter(Boolean).join(' ') ||
            identifier,
          phone: d.phone || (isPhone ? identifier : null),
          email: d.email || (!isPhone ? identifier : null),
          roles: d.roles || [],
          clientReference: d.clientReference || null,
        });
        return;
      }
    } catch (err) {
      console.warn('Contact lookup failed:', err);
    }
    setContactInfo({
      name: identifier,
      phone: identifier.startsWith('+') ? identifier : null,
      roles: [],
    });
  };

  // ─── Send SMS (Firebase callable) ──────────────────────────────────
  const handleSendSMS = async () => {
    if (!newMessage.trim() || !selectedThread || sending) return;
    setSending(true);
    try {
      const callable = httpsCallable(firebaseFunctions, 'sendSmsNotification');
      await callable({ to: selectedThread.contact, message: newMessage.trim() });
      setNewMessage('');
      toast.success(t.smsSent);
      // Refresh thread messages immediately
      loadSMSMessages(selectedThread.contact);
    } catch (err) {
      console.error('Send SMS error:', err);
      toast.error(t.smsError);
    } finally {
      setSending(false);
    }
  };

  // ─── Make call ─────────────────────────────────────────────────────
  const handleMakeCall = async (phone: string) => {
    await makeCall(phone);
  };
  const callLoading = deviceStatus === 'initializing' || activeCall?.status === 'connecting';

  // ─── Scroll to bottom ─────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [smsMessages]);

  // ─── Filtered threads ─────────────────────────────────────────────
  const filteredThreads = threads.filter(t => {
    // Tab filter
    if (inboxTab === 'chats' && t.type === 'call') return false;
    if (inboxTab === 'calls' && t.type !== 'call') return false;
    if (inboxTab === 'voicemails') {
      if (t.type !== 'call' || !t.isVoicemail) return false;
    }

    // Status filter
    if (inboxFilter === 'unread' && t.unreadCount === 0) return false;

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return t.contact.toLowerCase().includes(q) || t.lastMessage.toLowerCase().includes(q);
    }
    return true;
  });

  // Total unread
  const totalUnread = threads.filter(t => t.unreadCount > 0).length;
  const voicemailCount = threads.filter(t => t.isVoicemail).length;
  const unreadVoicemailCount = threads.filter(t => t.isVoicemail && t.unreadCount > 0).length;

  return (
    <div className="h-[calc(100vh-8rem)] flex rounded-2xl border border-white/10 overflow-hidden bg-[hsl(220,20%,10%)] font-['Geist',sans-serif]">
      {/* ═══ Left Mini-Sidebar ═══ */}
      <div className="w-16 bg-[hsl(220,20%,8%)] border-r border-white/10 flex flex-col items-center py-4 gap-1">
        <MiniNavBtn icon={<Inbox />} label={t.inbox} active={sidebarView === 'inbox'} badge={totalUnread} onClick={() => setSidebarView('inbox')} />
        <MiniNavBtn icon={<Search />} label={t.search} active={false} onClick={() => {}} />
        <MiniNavBtn icon={<Activity />} label={t.activity} active={sidebarView === 'activity'} onClick={() => setSidebarView('activity')} />
        <MiniNavBtn icon={<Grid3X3 />} label={t.dialpad} active={sidebarView === 'dialpad'} onClick={() => setSidebarView('dialpad')} />
        <MiniNavBtn icon={<PhoneMissed />} label={language === 'fr' ? 'Manqués' : 'Missed'} active={sidebarView === 'missed'} onClick={() => setSidebarView('missed')} />
        <MiniNavBtn icon={<Clock />} label={language === 'fr' ? 'Rendez-vous' : 'Bookings'} active={sidebarView === 'bookings'} onClick={() => setSidebarView('bookings')} />
        <Separator className="my-2 w-8 bg-white/10" />

        <MiniNavBtn icon={<Bot />} label={t.aiAgent} active={false} onClick={() => {}} />
        <MiniNavBtn icon={<Contact />} label={t.contacts} active={sidebarView === 'contacts'} onClick={() => setSidebarView('contacts')} />
        <MiniNavBtn icon={<BarChart3 />} label={t.marketing} active={sidebarView === 'marketing'} onClick={() => setSidebarView('marketing')} />
        <MiniNavBtn icon={<Users />} label={language === 'fr' ? 'Équipe' : 'Team'} active={sidebarView === 'agents'} onClick={() => setSidebarView('agents')} />

        <div className="flex-1" />

        <MiniNavBtn icon={<Settings />} label={t.settings} active={sidebarView === 'settings'} onClick={() => setSidebarView('settings')} />
      </div>

      {/* ═══ Thread List Panel ═══ */}
      <div className="w-80 border-r border-white/10 flex flex-col bg-[hsl(220,20%,11%)]">
        {/* Tabs: Chats / Calls */}
        <div className="px-3 pt-3 pb-2 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              <TabButton label={t.tabMessages} active={inboxTab === 'chats'} onClick={() => setInboxTab('chats')} icon={<MessageSquare className="w-3.5 h-3.5" />} />
              <TabButton label={t.tabCalls} active={inboxTab === 'calls'} onClick={() => setInboxTab('calls')} icon={<Phone className="w-3.5 h-3.5" />} />
              <TabButton label={`${t.tabVoicemail}${voicemailCount > 0 ? ` (${voicemailCount})` : ''}`} active={inboxTab === 'voicemails'} onClick={() => setInboxTab('voicemails')} icon={<Voicemail className="w-3.5 h-3.5" />} badge={unreadVoicemailCount} />
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white hover:bg-white/10">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-1.5">
            <FilterChip label={t.filterAll} active={inboxFilter === 'all'} onClick={() => setInboxFilter('all')} />
            <FilterChip label={t.filterUnread} active={inboxFilter === 'unread'} onClick={() => setInboxFilter('unread')} />
            <FilterChip label={t.filterAssigned} active={inboxFilter === 'assigned'} onClick={() => setInboxFilter('assigned')} />
            <button className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors">
              <Filter className="w-3 h-3" /> {t.filter}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <Input
              placeholder={t.searchConversations}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/20"
            />
          </div>
        </div>

        {/* Thread list */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-white/30">
             <Inbox className="w-8 h-8 mb-2" />
              <p className="text-xs">{t.noConversations}</p>
            </div>
          ) : (
            filteredThreads.map(thread => (
              <ThreadItem
                key={thread.id}
                thread={thread}
                active={selectedThread?.id === thread.id}
                onClick={() => selectThread(thread)}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* ═══ Main Conversation Area ═══ */}
      <div className="flex-1 flex flex-col bg-[hsl(220,18%,13%)]">
        {/* Softphone bar */}
        <SoftphoneBar
          deviceStatus={deviceStatus}
          activeCall={activeCall}
          incomingCall={incomingCall}
          formattedDuration={formattedDuration}
          onInitialize={initializeDevice}
          onHangUp={hangUp}
          onToggleMute={toggleMute}
          onAccept={acceptCall}
          onReject={rejectCall}
        />
        {sidebarView === 'bookings' ? (
          <div className="flex-1 p-6 overflow-auto">
            <PendingBookingsPanel />
          </div>
        ) : sidebarView === 'missed' ? (
          <div className="flex-1 p-6 overflow-auto">
            <MissedCallsDashboard onCallBack={handleMakeCall} />
          </div>
        ) : sidebarView === 'marketing' ? (
          <MarketingDashboard />
        ) : sidebarView === 'agents' ? (
          <AgentsOnlinePanel />
        ) : sidebarView === 'contacts' ? (
          <ContactsDirectory
            onCall={handleMakeCall}
            onSMS={(phone: string) => {
              const thread: UnifiedThread = {
                id: `sms-${phone}`,
                type: 'sms',
                contact: phone,
                lastMessage: '',
                lastMessageAt: new Date().toISOString(),
                unreadCount: 0,
                direction: 'outbound',
              };
              setSelectedThread(thread);
              setSidebarView('inbox');
              setInboxTab('chats');
              loadSMSMessages(phone);
            }}
            onNewContact={() => setShowMarketingForm(true)}
          />
        ) : sidebarView === 'dialpad' ? (
          <DialPadView
            onMakeCall={handleMakeCall}
            callLoading={callLoading}
            activeCall={activeCall}
            onHangUp={hangUp}
            recentCalls={threads.filter(t => t.type === 'call').slice(0, 10)}
            onStartSMS={(phone: string) => {
              const thread: UnifiedThread = {
                id: `sms-${phone}`,
                type: 'sms',
                contact: phone,
                lastMessage: '',
                lastMessageAt: new Date().toISOString(),
                unreadCount: 0,
                direction: 'outbound',
              };
              setSelectedThread(thread);
              setSidebarView('inbox');
              setInboxTab('chats');
              loadSMSMessages(phone);
            }}
            onSaveContact={(phone: string) => {
              setDialpadSavePhone(phone);
              setShowMarketingForm(true);
            }}
          />
        ) : !selectedThread && !meetingContext ? (
          <div className="flex-1 flex flex-col items-center justify-center text-white/20">
            <MessageSquare className="w-16 h-16 mb-4" />
            <p className="text-sm font-medium">{t.selectConversation}</p>
            <p className="text-xs mt-1">{t.selectFromLeft}</p>
          </div>
        ) : !selectedThread && meetingContext ? (
          /* ═══ Meeting Context Card (from Calendar redirect) ═══ */
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  meetingContext.format === 'video' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {meetingContext.format === 'video' ? <MessageSquare className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{meetingContext.meetingTitle}</p>
                  <p className="text-[11px] text-white/40">
                    {meetingContext.format === 'video' ? t.videoConference : t.phoneCall}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-7 w-7 text-white/40 hover:text-white"
                  onClick={() => setSearchParams({})}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* ═══ PRIMARY ACTION — Lien principal cliquable ═══ */}
              {(() => {
                const callablePhoneRaw = meetingContext.phone || meetingContext.participants.find(p => p.phone)?.phone;
                const callablePhone = callablePhoneRaw?.replace(/\s+/g, '');
                const meetingUrl = meetingContext.meetingUrl?.trim();

                if (meetingContext.format === 'phone') {
                  if (callablePhone) {
                    return (
                      <div className="px-5 py-5 border-b border-white/10 bg-emerald-500/5 space-y-2">
                        <Button
                          size="lg"
                          className="w-full gap-3 h-14 text-base bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-900/30"
                          onClick={() => handleMakeCall(callablePhone)}
                          disabled={callLoading}
                        >
                          <Phone className="w-5 h-5" />
                          {t.dial} {callablePhoneRaw}
                        </Button>
                      </div>
                    );
                  }

                  return (
                    <div className="px-5 py-4 border-b border-white/10 bg-amber-500/5">
                      <p className="text-xs text-amber-300">
                        {t.noPhoneAvailable}
                      </p>
                    </div>
                  );
                }

                if (meetingContext.format === 'video' && meetingUrl) {
                  return (
                    <div className="px-5 py-5 border-b border-white/10 bg-blue-500/5">
                      <Button
                        asChild
                        size="lg"
                        className="w-full gap-3 h-14 text-base bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-900/30"
                      >
                        <a href={meetingUrl} target="_blank" rel="noopener noreferrer">
                          <MessageSquare className="w-5 h-5" />
                          {t.joinVideoConference}
                        </a>
                      </Button>
                      <a
                        href={meetingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-[10px] text-blue-400/80 mt-2 text-center break-all underline underline-offset-2 hover:text-blue-300"
                      >
                        {meetingUrl}
                      </a>
                    </div>
                  );
                }

                return null;
              })()}

              {/* Participants */}
              <div className="px-5 py-3 space-y-2">
                <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">
                  Participants ({meetingContext.participants.length || 1})
                </p>
                {meetingContext.participants.length > 0 ? (
                  meetingContext.participants.map((p, i) => {
                    const participantPhone = p.phone?.replace(/\s+/g, '');
                    return (
                      <div key={i} className="py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                              <User className="w-3.5 h-3.5 text-white/50" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-white">{p.name}</p>
                              <p className="text-[10px] text-white/40">{p.role}</p>
                            </div>
                          </div>
                          {participantPhone && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400"
                              onClick={() => handleMakeCall(participantPhone)}
                              disabled={callLoading}
                            >
                              <Phone className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        {participantPhone && (
                          <div className="ml-9 mt-1">
                            <button
                              onClick={() => handleMakeCall(participantPhone)}
                              disabled={callLoading}
                              className="text-[10px] text-emerald-400/80 flex items-center gap-1 hover:text-emerald-300 underline underline-offset-2"
                            >
                              <Phone className="w-2.5 h-2.5" /> {p.phone}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center gap-2 py-1.5">
                    <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-white/50" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">{meetingContext.name}</p>
                      {meetingContext.phone && (
                        <button
                          onClick={() => handleMakeCall(meetingContext.phone!.replace(/\s+/g, ''))}
                          disabled={callLoading}
                          className="text-[10px] text-emerald-400/80 hover:text-emerald-300 underline underline-offset-2"
                        >
                          {meetingContext.phone}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Secondary actions */}
              {(() => {
                const callablePhone = (meetingContext.phone || meetingContext.participants.find(p => p.phone)?.phone)?.replace(/\s+/g, '');
                const meetingUrl = meetingContext.meetingUrl?.trim();
                const contactEmail = meetingContext.email || meetingContext.participants.find(p => p.email)?.email;
                const secondaryActions = [];

                if (meetingContext.format === 'video' && callablePhone) {
                  secondaryActions.push(
                    <Button key="call" size="sm" variant="outline" className="flex-1 gap-2 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10" onClick={() => handleMakeCall(callablePhone)} disabled={callLoading}>
                      <Phone className="w-3.5 h-3.5" /> {t.callAction}
                    </Button>
                  );
                }

                if (meetingContext.format === 'phone' && meetingUrl) {
                  secondaryActions.push(
                    <Button key="video" size="sm" variant="outline" className="flex-1 gap-2 border-blue-500/20 text-blue-400 hover:bg-blue-500/10" asChild>
                      <a href={meetingUrl} target="_blank" rel="noopener noreferrer">
                        <MessageSquare className="w-3.5 h-3.5" /> {t.video}
                      </a>
                    </Button>
                  );
                }

                if (contactEmail) {
                  secondaryActions.push(
                    <Button key="email" size="sm" variant="outline" className="flex-1 gap-2 border-white/10 text-white/70 hover:text-white" asChild>
                      <a href={`mailto:${contactEmail}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> {t.emailAction}
                      </a>
                    </Button>
                  );
                }

                if (secondaryActions.length === 0) return null;
                return <div className="px-5 py-3 border-t border-white/10 flex gap-2">{secondaryActions}</div>;
              })()}
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="h-14 px-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  {selectedThread.type === 'call' ? (
                    <Phone className="w-4 h-4 text-white/70" />
                  ) : (
                    <User className="w-4 h-4 text-white/70" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{formatPhone(selectedThread.contact)}</p>
                  <p className="text-[10px] text-white/40">
                    {selectedThread.type === 'sms' ? t.sms : selectedThread.type === 'call' ? t.call : t.chat}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Call button */}
                {!activeCall ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-emerald-400 hover:bg-emerald-500/10"
                    onClick={() => handleMakeCall(selectedThread.contactId || selectedThread.contact)}
                    disabled={callLoading}
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-400 hover:bg-red-500/10"
                    onClick={() => hangUp()}
                    disabled={callLoading}
                  >
                    <PhoneOff className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white/50 hover:text-white hover:bg-white/10"
                  onClick={() => setShowContactPanel(!showContactPanel)}
                >
                  <Contact className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white/50 hover:text-white hover:bg-white/10">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Messages area */}
            <ScrollArea className="flex-1 p-4">
              {selectedThread.type === 'sms' ? (
                <div className="space-y-3">
                  {smsMessages.map(msg => {
                    const isOutbound = msg.direction === 'outbound';
                    return (
                      <div key={msg.id} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                          isOutbound
                            ? 'bg-[hsl(142,50%,40%)] text-white rounded-br-md'
                            : 'bg-white/10 text-white rounded-bl-md'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                          <div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'justify-end' : ''}`}>
                            <span className="text-[10px] text-white/50">
                              {format(new Date(msg.created_at), 'HH:mm')}
                            </span>
                            {isOutbound && <StatusIcon status={msg.status} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : selectedThread.type === 'call' ? (
                <CallDetailView thread={selectedThread} onMakeCall={handleMakeCall} />
              ) : null}
            </ScrollArea>

            {/* Input area (SMS only) */}
            {selectedThread.type === 'sms' && (
              <div className="p-3 border-t border-white/10 relative">
                <SMSSnippetsPanel
                  isOpen={showSnippets}
                  onClose={() => setShowSnippets(false)}
                  onSelectSnippet={(body) => setNewMessage(prev => prev + body)}
                />
                <div className="flex gap-2 items-end">
                  <div className="flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[10px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                        onClick={() => setShowSnippets(!showSnippets)}
                      >
                        <Zap className="w-3 h-3 mr-1" /> Snippets
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[10px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        onClick={() => setShowMarketingForm(true)}
                      >
                        <UserPlus className="w-3 h-3 mr-1" /> {t.logContact}
                      </Button>
                    </div>
                    <Textarea
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder={t.writeMessage}
                      className="resize-none min-h-[40px] max-h-[100px] text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30"
                      rows={1}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendSMS();
                        }
                      }}
                    />
                  </div>
                  <Button
                    size="icon"
                    onClick={handleSendSMS}
                    disabled={!newMessage.trim() || sending}
                    className="h-10 w-10 flex-shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ Contact Detail Panel ═══ */}
      {showContactPanel && selectedThread && (
        <div className="w-72 border-l border-white/10 bg-[hsl(220,20%,11%)] flex flex-col">
          <div className="p-4 flex items-center justify-between border-b border-white/10">
            <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">{t.contact}</span>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-white/40 hover:text-white" onClick={() => setShowContactPanel(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="p-4 flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-3">
              <User className="w-8 h-8 text-white/30" />
            </div>
            <p className="text-sm font-medium text-white">{t.addName}</p>
            <p className="text-[11px] text-white/40 mt-0.5">{formatPhone(selectedThread.contact)}</p>

            {/* Quick actions */}
            <div className="flex gap-3 mt-4">
              <button
                className="flex flex-col items-center gap-1"
                onClick={() => handleMakeCall(selectedThread.contactId || selectedThread.contact)}
              >
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors">
                  <Phone className="w-4 h-4 text-white/70" />
                </div>
                <span className="text-[10px] text-white/40">{t.callAction}</span>
              </button>
              <button className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors">
                  <MessageCircle className="w-4 h-4 text-white/70" />
                </div>
                <span className="text-[10px] text-white/40">{t.messageAction}</span>
              </button>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* Contact fields */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              <ContactField icon={<Contact className="w-3.5 h-3.5" />} label={t.company} value={t.addCompany} />
              <ContactField icon={<User className="w-3.5 h-3.5" />} label={t.role} value={t.addRole} />
              <ContactField icon={<Phone className="w-3.5 h-3.5" />} label={t.phone} value={formatPhone(selectedThread.contact)} />
              <ContactField icon={<MessageCircle className="w-3.5 h-3.5" />} label={t.email} value={t.addEmail} />
              <ContactField icon={<Hash className="w-3.5 h-3.5" />} label={t.status} value={t.addTag} />
            </div>

            <button
              className="mt-4 flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 transition-colors"
              onClick={() => setShowMarketingForm(true)}
            >
              <UserPlus className="w-3 h-3" /> {t.saveAsContact}
            </button>
          </ScrollArea>
        </div>
      )}

      {/* Marketing contact form dialog */}
      <MarketingContactForm
        isOpen={showMarketingForm}
        onClose={() => { setShowMarketingForm(false); setDialpadSavePhone(undefined); }}
        prefillPhone={dialpadSavePhone || (selectedThread?.contact?.startsWith('+') ? selectedThread.contact : undefined)}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════

function MiniNavBtn({ icon, label, active, badge, onClick }: {
  icon: React.ReactNode; label: string; active: boolean; badge?: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
        active ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/5'
      }`}
      title={label}
    >
      {React.cloneElement(icon as React.ReactElement, { className: 'w-4.5 h-4.5' })}
      {badge && badge > 0 ? (
        <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function TabButton({ label, active, onClick, icon, badge }: {
  label: string; active: boolean; onClick: () => void; icon: React.ReactNode; badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60 hover:bg-white/5'
      }`}
    >
      {icon} {label}
      {badge && badge > 0 ? (
        <span className="min-w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
        active ? 'bg-white/10 text-white' : 'text-white/30 hover:text-white/50 hover:bg-white/5'
      }`}
    >
      {label}
    </button>
  );
}

function ThreadItem({ thread, active, onClick }: { thread: UnifiedThread; active: boolean; onClick: () => void }) {
  const icon = thread.type === 'call' ? (
    thread.isVoicemail ? <Voicemail className="w-4 h-4 text-amber-400" /> :
    thread.callStatus === 'completed' ? <PhoneIncoming className="w-4 h-4 text-emerald-400" /> :
    thread.callStatus === 'missed' || thread.callStatus === 'no-answer' ? <PhoneMissed className="w-4 h-4 text-red-400" /> :
    <Phone className="w-4 h-4 text-white/50" />
  ) : thread.type === 'sms' ? (
    <MessageCircle className="w-4 h-4 text-blue-400" />
  ) : (
    <Users className="w-4 h-4 text-purple-400" />
  );

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors flex items-start gap-3 ${
        active ? 'bg-white/[0.08]' : ''
      }`}
    >
      <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white truncate">{formatPhone(thread.contact)}</span>
          <span className="text-[10px] text-white/30 flex-shrink-0">
            {format(new Date(thread.lastMessageAt), 'HH:mm')}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {thread.type === 'call' && <CallDirectionIcon status={thread.callStatus} />}
          <p className="text-xs text-white/40 truncate">{thread.lastMessage}</p>
        </div>
      </div>
      {thread.unreadCount > 0 && (
        <span className="min-w-5 h-5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 flex-shrink-0 mt-1">
          {thread.unreadCount}
        </span>
      )}
    </button>
  );
}

function CallDirectionIcon({ status }: { status?: string }) {
  if (status === 'completed') return <CheckCheck className="w-3 h-3 text-emerald-400 flex-shrink-0" />;
  if (status === 'missed' || status === 'no-answer') return <PhoneMissed className="w-3 h-3 text-red-400 flex-shrink-0" />;
  if (status === 'voicemail') return <Voicemail className="w-3 h-3 text-amber-400 flex-shrink-0" />;
  return null;
}

function CallDetailView({ thread, onMakeCall }: { thread: UnifiedThread; onMakeCall?: (phone: string) => void }) {
  const { language } = useLanguage();
  const t = COMM_HUB_T[language];
  const dateLocale = language === 'fr' ? fr : enUS;
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const isVoicemail = thread.isVoicemail || thread.callStatus === 'voicemail';
  const hasAudio = !!thread.voicemailUrl;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${
        isVoicemail ? 'bg-amber-500/20' :
        thread.callStatus === 'completed' ? 'bg-emerald-500/20' :
        thread.callStatus === 'missed' ? 'bg-red-500/20' : 'bg-white/10'
      }`}>
        {isVoicemail ? (
          <Voicemail className="w-6 h-6 text-amber-400" />
        ) : (
          <Phone className={`w-6 h-6 ${
            thread.callStatus === 'completed' ? 'text-emerald-400' :
            thread.callStatus === 'missed' ? 'text-red-400' : 'text-white/50'
          }`} />
        )}
      </div>
      <p className="text-sm font-semibold text-white">
        {isVoicemail ? t.voiceMessage : thread.callStatus === 'completed' ? t.callEnded : thread.callStatus === 'missed' ? t.missedCall : t.call}
      </p>
      {thread.contactId && (
        <p className="text-xs text-emerald-400 mt-1 font-mono">
          📞 {formatPhone(thread.contactId)}
        </p>
      )}
      <p className="text-xs text-white/40 mt-1">
        {thread.callDuration ? `${t.duration}: ${Math.floor(thread.callDuration / 60)}:${(thread.callDuration % 60).toString().padStart(2, '0')}` : ''}
      </p>
      <p className="text-xs text-white/30 mt-0.5">
        {format(new Date(thread.lastMessageAt), 'dd MMM yyyy, HH:mm', { locale: dateLocale })}
      </p>

      {/* Audio player */}
      {hasAudio ? (
        <div className="mt-6 w-full max-w-sm">
          <div className="bg-white/5 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center hover:bg-amber-500/30 transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4 text-amber-400" />
                ) : (
                  <Play className="w-4 h-4 text-amber-400 ml-0.5" />
                )}
              </button>
              <div className="flex-1">
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full w-0 bg-amber-400/60 rounded-full transition-all" />
                </div>
                <p className="text-[10px] text-white/30 mt-1">
                  {thread.callDuration ? `${Math.floor(thread.callDuration / 60)}:${(thread.callDuration % 60).toString().padStart(2, '0')}` : '0:00'}
                </p>
              </div>
            </div>
            <audio
              ref={audioRef}
              src={thread.voicemailUrl}
              onEnded={() => setIsPlaying(false)}
              preload="metadata"
            />
          </div>
        </div>
      ) : isVoicemail ? (
        <div className="mt-6 w-full max-w-sm bg-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>{t.waitingRecording}</span>
          </div>
        </div>
      ) : (
        <div className="mt-6 w-full max-w-sm bg-white/5 rounded-xl p-3 flex items-center gap-3">
          <button className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors">
            <Play className="w-4 h-4 text-white/70 ml-0.5" />
          </button>
          <div className="flex-1 h-1 bg-white/10 rounded-full">
            <div className="h-full w-0 bg-white/40 rounded-full" />
          </div>
          <span className="text-[10px] text-white/30">0:00</span>
        </div>
      )}

      {/* Transcription */}
      {thread.transcriptionText ? (
        <div className="mt-4 w-full max-w-sm">
          <div className="bg-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-[11px] font-medium text-blue-400 uppercase tracking-wider">{t.transcription}</span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">{thread.transcriptionText}</p>
          </div>
        </div>
      ) : isVoicemail ? (
        <div className="mt-4 flex items-center gap-2 text-xs text-white/30">
          <Bot className="w-3.5 h-3.5" />
          <span>{t.transcriptionProcessing}</span>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2 text-xs text-white/30">
          <Bot className="w-3.5 h-3.5" />
          <span>{t.summaryGenerating}</span>
        </div>
      )}

      {/* Callback button */}
      {onMakeCall && thread.contactId && (
        <Button
          className="mt-6 gap-2 bg-emerald-600 hover:bg-emerald-500 text-white"
          onClick={() => onMakeCall(thread.contactId!)}
        >
          <Phone className="w-4 h-4" />
          {t.callback} {formatPhone(thread.contactId)}
        </Button>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'delivered') return <CheckCheck className="w-3 h-3 text-emerald-400" />;
  if (status === 'sent') return <CheckCheck className="w-3 h-3 text-white/30" />;
  if (status === 'failed') return <AlertCircle className="w-3 h-3 text-red-400" />;
  return <Circle className="w-3 h-3 text-white/20" />;
}

function ContactField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-white/30">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-white/30 uppercase tracking-wider">{label}</p>
        <p className="text-xs text-white/60 truncate">{value}</p>
      </div>
    </div>
  );
}

function DialPadView({ onMakeCall, callLoading, activeCall, onHangUp, recentCalls, onStartSMS, onSaveContact }: {
  onMakeCall: (phone: string) => void;
  callLoading: boolean;
  activeCall: any;
  onHangUp: () => void;
  recentCalls: UnifiedThread[];
  onStartSMS: (phone: string) => void;
  onSaveContact: (phone: string) => void;
}) {
  const { language } = useLanguage();
  const t = COMM_HUB_T[language];
  const [dialNumber, setDialNumber] = useState('');
  const [mode, setMode] = useState<'call' | 'sms'>('call');

  const handleDigit = (digit: string) => {
    setDialNumber(prev => prev + digit);
  };

  const handleBackspace = () => {
    setDialNumber(prev => prev.slice(0, -1));
  };

  const sanitizeNumber = (num: string) => {
    let cleaned = num.replace(/\D/g, '');
    if (cleaned.length === 10) cleaned = '+1' + cleaned;
    else if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
    return cleaned;
  };

  const handleAction = () => {
    if (!dialNumber.trim()) return;
    const num = sanitizeNumber(dialNumber);
    if (mode === 'sms') {
      onStartSMS(num);
    } else {
      onMakeCall(num);
    }
  };

  // Physical keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        setDialNumber(prev => prev + e.key);
      } else if (e.key === '+') {
        e.preventDefault();
        setDialNumber(prev => prev + '+');
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setDialNumber(prev => prev.slice(0, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleAction();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAction]);

  const displayNumber = (() => {
    const cleaned = dialNumber.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  })();

  const digits = [
    { main: '1', sub: '' },
    { main: '2', sub: 'ABC' },
    { main: '3', sub: 'DEF' },
    { main: '4', sub: 'GHI' },
    { main: '5', sub: 'JKL' },
    { main: '6', sub: 'MNO' },
    { main: '7', sub: 'PQRS' },
    { main: '8', sub: 'TUV' },
    { main: '9', sub: 'WXYZ' },
    { main: '*', sub: '' },
    { main: '0', sub: '+' },
    { main: '#', sub: '' },
  ];

  const isCallActive = !!activeCall;

  return (
    <div className="flex-1 flex flex-col items-center overflow-y-auto py-6">
      <div className="w-full max-w-xs space-y-5">
        {/* Mode toggle: Call / SMS */}
        <div className="flex justify-center gap-1 bg-white/5 rounded-xl p-1">
          <button
            onClick={() => setMode('call')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'call' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >
            <Phone className="w-3.5 h-3.5" /> {t.dialCall}
          </button>
          <button
            onClick={() => setMode('sms')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              mode === 'sms' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" /> {t.dialSMS}
          </button>
        </div>

        {/* Number display */}
        <div className="text-center px-4 min-h-[60px] flex items-center justify-center">
          <p className={`font-mono tracking-wider text-white transition-all ${
            dialNumber.length > 0 ? 'text-3xl font-light' : 'text-lg text-white/30'
          }`}>
            {dialNumber.length > 0 ? displayNumber : t.enterNumber}
          </p>
        </div>

        {/* Dial pad grid */}
        <div className="grid grid-cols-3 gap-3 px-2">
          {digits.map(({ main, sub }) => (
            <button
              key={main}
              onClick={() => handleDigit(main)}
              className="h-16 rounded-2xl bg-white/[0.07] hover:bg-white/[0.12] active:bg-white/[0.18] transition-all flex flex-col items-center justify-center gap-0.5 select-none"
            >
              <span className="text-2xl font-light text-white">{main}</span>
              {sub && <span className="text-[9px] font-semibold text-white/30 tracking-[0.2em]">{sub}</span>}
            </button>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-6 px-2">
          {/* Hang up (visible only during active call) */}
          {isCallActive ? (
            <button
              onClick={onHangUp}
              className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-all shadow-lg shadow-red-900/40"
            >
              <PhoneOff className="w-5 h-5 text-white" />
            </button>
          ) : (
            <div className="w-14" />
          )}

          {/* Call / SMS button */}
          <button
            onClick={handleAction}
            disabled={!dialNumber.trim() || callLoading}
            className={`w-16 h-16 rounded-full disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all shadow-lg ${
              mode === 'sms'
                ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-900/40'
                : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/40'
            }`}
          >
            {mode === 'sms' ? (
              <Send className="w-5 h-5 text-white" />
            ) : (
              <Phone className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Backspace */}
          <button
            onClick={handleBackspace}
            disabled={!dialNumber}
            className="w-14 h-14 rounded-full flex items-center justify-center text-white/40 hover:text-white/70 disabled:opacity-0 transition-all"
          >
            <Delete className="w-5 h-5" />
          </button>
        </div>

        {/* Save as contact */}
        {dialNumber.length >= 7 && (
          <div className="flex justify-center px-2">
            <button
              onClick={() => {
                const num = sanitizeNumber(dialNumber);
                onSaveContact(num);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-white/50 hover:text-white/70 text-xs"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {t.saveContact}
            </button>
          </div>
        )}

        {/* Recent calls */}
        {recentCalls.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold px-2 mb-2">
              {t.recentCalls}
            </p>
            <div className="space-y-0.5">
              {recentCalls.map(call => (
                <button
                  key={call.id}
                  onClick={() => {
                    const phone = call.contactId || call.contact;
                    setDialNumber(phone.replace('+1', '').replace('+', ''));
                  }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    {call.callStatus === 'completed' ? (
                      <PhoneIncoming className="w-3.5 h-3.5 text-emerald-400" />
                    ) : call.callStatus === 'missed' || call.callStatus === 'no-answer' ? (
                      <PhoneMissed className="w-3.5 h-3.5 text-red-400" />
                    ) : call.isVoicemail ? (
                      <Voicemail className="w-3.5 h-3.5 text-amber-400" />
                    ) : (
                      <Phone className="w-3.5 h-3.5 text-white/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{formatPhone(call.contactId || call.contact)}</p>
                    <p className="text-[10px] text-white/30 truncate">{call.lastMessage}</p>
                  </div>
                  <span className="text-[10px] text-white/20 flex-shrink-0">
                    {format(new Date(call.lastMessageAt), 'HH:mm')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function formatPhone(phone: string) {
  if (phone.length === 12 && phone.startsWith('+1')) {
    return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
  }
  return phone;
}

function formatCallStatus(status: string, duration: number | null, t?: Record<string, string>): string {
  const labels = t || COMM_HUB_T.fr;
  if (status === 'completed' && duration) {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    return `${labels.callCompleted} · ${m}:${s.toString().padStart(2, '0')}`;
  }
  if (status === 'missed' || status === 'no-answer') return labels.callMissed;
  if (status === 'ringing') return labels.ringing;
  if (status === 'busy') return labels.busy;
  if (status === 'voicemail') return labels.voicemailMsg;
  return `${labels.callPrefix} ${status}`;
}